import { memo, useState } from "react";
import { Brand } from "@/data/brands";
import { getScoreColors } from "@/utils/scoreColors";
import { m } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

interface BrandDotProps {
  brand: Brand;
  x: number;
  visualY: number;
  showLogo: boolean;
  isHighlighted: boolean;
  isSearching: boolean;
  isSelected: boolean;
  index: number;
  onHover: (brand: Brand | null) => void;
  onClick: (brandId: string) => void;
  onSelect: (brand: Brand) => void;
}

const BrandDot = memo(({
  brand, x, visualY, showLogo, isHighlighted, isSearching, isSelected, index, onHover, onClick, onSelect,
}: BrandDotProps) => {
  const { language } = useLanguage();
  const [revealed, setRevealed] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const scoreColors = getScoreColors(brand.overallScore);
  const shouldShowLogo = showLogo || revealed;
  const dimmed = isSearching && !isHighlighted;
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!shouldShowLogo) { setRevealed(true); return; }
    if (isMobile) {
      if (isSelected) { setIsActive(true); onClick(brand.id); } else { onSelect(brand); }
    } else { setIsActive(true); onClick(brand.id); }
  };

  if (!shouldShowLogo) {
    return (
      <m.button
        onClick={handleClick}
        onMouseEnter={() => onHover(brand)}
        onMouseLeave={() => onHover(null)}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: dimmed ? 0.1 : 0.7, scale: 1, transition: { duration: 0.3, delay: index * 0.004 } }}
        whileHover={{ scale: 1.8, opacity: 1, transition: { duration: 0.12 } }}
        className="absolute w-1.5 h-1.5 md:w-4 md:h-4 -translate-x-1/2 -translate-y-1/2 rounded-full cursor-pointer border-0 md:border md:border-white/60 md:shadow-sm"
        style={{ left: `${x * 100}%`, bottom: `${visualY * 100}%`, backgroundColor: scoreColors.hex }}
        title={`${brand.name} — ${brand.overallScore}/100`}
      />
    );
  }

  return (
    <m.button
      onClick={handleClick}
      onMouseEnter={() => onHover(brand)}
      onMouseLeave={() => onHover(null)}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: dimmed ? 0.15 : 1, scale: 1, transition: { duration: 0.4, delay: index * 0.006 } }}
      whileHover={{ scale: 1.25, zIndex: 50, transition: { duration: 0.15 } }}
      whileTap={{ zIndex: 60, scale: 1.15 }}
      className="group absolute w-6 h-6 md:w-12 md:h-12 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center bg-white rounded-full shadow-md border md:border-2 border-white hover:shadow-xl hover:border-primary/30 active:shadow-lg active:shadow-black/20 transition-shadow duration-200 cursor-pointer overflow-visible p-0.5 md:p-1.5"
      style={{ left: `${x * 100}%`, bottom: `${visualY * 100}%`, zIndex: isActive ? 50 : 10 }}
      title={`${brand.name} — ${brand.overallScore}/100`}
    >
      <img
        src={brand.logo}
        alt={language === 'fr' ? `Logo de la marque de mode ${brand.name}` : `${brand.name} fashion brand logo`}
        className="w-full h-full object-contain rounded-full"
        loading="lazy"
        width={48}
        height={48}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = "none";
          const parent = target.parentElement;
          if (parent) { parent.textContent = ''; const span = document.createElement('span'); span.className = 'text-[7px] md:text-xs font-bold text-foreground'; span.textContent = brand.name[0]; parent.appendChild(span); }
        }}
      />
      <m.span
        className="absolute -top-1 -right-1 md:-top-1.5 md:-right-1.5 w-4 h-4 md:w-6 md:h-6 rounded-full text-[7px] md:text-[10px] font-bold flex items-center justify-center text-white shadow-md ring-1 md:ring-2 ring-white opacity-0 group-hover:opacity-100"
        style={{ backgroundColor: scoreColors.hex }}
        initial={false}
        transition={{ duration: 0.15 }}
      >
        {brand.overallScore}
      </m.span>
    </m.button>
  );
});

BrandDot.displayName = "BrandDot";

export default BrandDot;
